/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* cookie banner content update custom js*/

document.addEventListener('DOMContentLoaded', function() {
  // Function to setup banner interactions
  function setupBanner(bannerDialog) {
    if (!bannerDialog) return;

    // Create and move policy links container
    const policyLink = bannerDialog.querySelector('#shopify-pc__banner__body-policy-link');
    const buttonsContainer = bannerDialog.querySelector('.shopify-pc__banner__btns');
    
    if (policyLink && buttonsContainer) {
      const policyDiv = document.createElement('div');
      policyDiv.className = 'shopify-pc__banner__policy-link-container';
      
      // Create terms and conditions link
      const termsLink = document.createElement('a');
      termsLink.href = '/pages/terms-and-conditions';
      termsLink.textContent = 'Terms & Conditions';
      termsLink.className = 'shopify-pc__banner__body-policy-link';
      termsLink.target = '_blank';
      termsLink.rel = 'noopener noreferrer';
      
      // Add both links to the container
      policyDiv.appendChild(termsLink);
      policyDiv.appendChild(policyLink);
      
      // Insert container after buttons
      buttonsContainer.parentNode.insertBefore(policyDiv, buttonsContainer.nextSibling);
    }

    // Add event listeners
    const acceptButton = bannerDialog.querySelector('#shopify-pc__banner__btn-accept');
    if (acceptButton) {
      acceptButton.addEventListener('click', function(e) {
        e.preventDefault();
        document.body.classList.remove('cookie-banner-visible');
      });
    }

    const rejectButton = bannerDialog.querySelector('#shopify-pc__banner__btn-decline');
    if (rejectButton) {
      rejectButton.addEventListener('click', function(e) {
        e.preventDefault();
        document.body.classList.remove('cookie-banner-visible');
      });
    }

    const bannerBody = bannerDialog.querySelector('.shopify-pc__banner__body');
    if (bannerBody) {
      document.body.classList.add('cookie-banner-visible');
    }
  }

  // Watch for banner insertion with optimized MutationObserver
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        const banner = document.getElementById('shopify-pc__banner');
        if (banner) {
          setupBanner(banner);
          observer.disconnect(); // Stop observing after we find the banner
        }
      }
    });
  });

  // Only observe body for added nodes
  observer.observe(document.body, { childList: true });
});

/* cookie banner content update custom js*/ 

// Add geolocation check for customers outside of Philippines
document.addEventListener('DOMContentLoaded', function() {
  // Check if notice was previously dismissed
  if (localStorage.getItem('outsidePhNoticeDismissed') === 'true') {
      return;
  } 

  // Get existing elements
  const overlay = document.querySelector('.geolocation-overlay');
  const modal = document.querySelector('.geolocation-modal');

  // Add event listeners if they haven't been added yet
  if (!modal.querySelector('.global-website-btn').hasAttribute('data-setup')) {
    modal.querySelector('.global-website-btn').addEventListener('click', () => {
      window.location.href = 'https://www.global.jdsports.com/';
      //window.open("https://www.global.jdsports.com/", "_blank");
    });
    modal.querySelector('.global-website-btn').setAttribute('data-setup', 'true');
  }

  if (!modal.querySelector('.stay-here-btn').hasAttribute('data-setup')) {
    modal.querySelector('.stay-here-btn').addEventListener('click', () => {
      overlay.classList.remove('active');
      modal.classList.remove('active');
      localStorage.setItem('outsidePhNoticeDismissed', 'true');
    });
    modal.querySelector('.stay-here-btn').setAttribute('data-setup', 'true');
  }

  /*if (!overlay.hasAttribute('data-setup')) {
      overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
              overlay.classList.remove('active');
              modal.classList.remove('active');
          }
      });
      overlay.setAttribute('data-setup', 'true');
  }*/

  // Helper: wait until window.Shopify and required fields are ready
  function waitForShopify(maxWaitMs = 7000, intervalMs = 100) {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const ready = !!(
          window.Shopify &&
          typeof window.Shopify.country === 'string' &&
          window.Shopify.country.length > 0
        );
        if (ready) return resolve(true);
        if (Date.now() - start >= maxWaitMs) return resolve(false);
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  // Get browsing context suggestions to check if customer is outside PH
  waitForShopify().then((available) => {
    fetch(
      window.Shopify.routes.root
      + 'browsing_context_suggestions.json'
      + '?country[enabled]=true'
      + `&country[exclude]=${window.Shopify.country}`
      + '&language[enabled]=true'
      + `&language[exclude]=${window.Shopify.language}`,
      {
        headers: {
          'Accept': 'application/json'
        }
      }
    )
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      // If there are suggestions, it means the customer is outside PH
      if (data.detected_values.country.handle != 'PH') {
        overlay.classList.add('active');
        modal.classList.add('active');

        // Update country name in modal content
        const countryName = data.detected_values.country_name;
        const modalContent = modal.querySelector('.modal-content p');
        if (modalContent) {
          // Replace 'Country Origin' with the actual country name
          const text = modalContent.innerHTML;
          if (text.includes('(Country Origin)')) {
            modalContent.innerHTML = text.replace('(Country Origin)', countryName);
          }
        }
      }
    })
    .catch(() => {
      // If API fails, show modal as fallback
      overlay.classList.add('active');
      modal.classList.add('active');
    });
  });
});

// custom country popup js
document.addEventListener('DOMContentLoaded', () => {
    // Store DOM elements
    const trigger = document.querySelector('.store-finder-popup-trigger');
    const popupContainer = document.querySelector('.store-finder-popup-container');
    const popupContainerClose = document.querySelector('.popup-modal__toggle');
    const hasBackground = !popupContainer?.classList.contains('header');
    const popupContainerBg = hasBackground ? document.querySelector('.store-finder-popup-background') : null;

    // Toggle popup visibility
    const togglePopup = (show) => {
        if (!popupContainer) return;
        
        const action = show ? 'add' : 'remove';
        trigger?.classList[action]('active');
        popupContainer.classList[action]('active');
        if (hasBackground) {
            popupContainerBg?.classList[action]('active');
        }
    };

    // Handle click events
    const handleClick = (e) => {
        e.preventDefault();
        if (e.target.closest('.store-finder-popup-trigger')) {
            togglePopup(true);
        } else if (e.target.closest('.popup-modal__toggle') || 
            (hasBackground && e.target.closest('.store-finder-popup-background')) ||
            (!hasBackground && e.target.closest('.store-finder-popup-container'))) {
            togglePopup(false);
        }
    };

    // Add event listeners directly to specific elements
    if (trigger) {
        trigger.addEventListener('click', handleClick);
    }
    if (popupContainerClose) {
        popupContainerClose.addEventListener('click', handleClick);
    }
    if (hasBackground && popupContainerBg) {
        popupContainerBg.addEventListener('click', handleClick);
    }
}); 


if((window.location.href).includes('/pages/swym-wishlist')) { 
  setInterval(() => {
      const wishlitItemCount = document.querySelector('.swym-wishlist-header-counter');
      if (wishlitItemCount && parseInt(wishlitItemCount.dataset.count) > 0) {
        document.querySelector('#swym-wishlist-container-to-render')?.classList.add('active');
        document.querySelector('.wishlist-empty-wrapper')?.classList.remove('active');
        } else if (wishlitItemCount && parseInt(wishlitItemCount.dataset.count) === 0){
        document.querySelector('#swym-wishlist-container-to-render')?.classList.remove('active');
        document.querySelector('#swym-wishlist-container-to-render')?.classList.add('empty');
        document.querySelector('.wishlist-empty-wrapper')?.classList.add('active');
        }
    }, 1000); 
}
if((window.location.href).includes('/apps/parcelpanel')) {
      // Append html to parcel panel app 
   if (window.innerWidth <= 768) {
    const parcePanel = setInterval(() => {
      const container = document.querySelector('.pp_tracking_form_in .pp_tracking_form_order[data-v-e799b34a]');

      if (container) {
        const htmlToAppend = `
          <div class="pp_tracking_line_center mobile_tracking_line">
            <div class="pp_tracking_word">Or</div>
          </div>
        `;
        container.insertAdjacentHTML('afterend', htmlToAppend);
        clearInterval(parcePanel);
      }
    }, 1000);
  }
   setInterval(() => {
    var parcleTrackerTitle = document.querySelector('.pp_tracking_result_title');
    var has_already_info = document.querySelector('.pp_tracking_result_info_wrapper');
    var visibleTrackingResults = document.querySelectorAll('.pp_tracking_result');
    let trackingNumber = "" 
    let expectedDeliveryDate = "";
    visibleTrackingResults?.forEach(function(result) {
      if (getComputedStyle(result).display !== 'none') {
        trackingNumber = result.querySelector('.pp_tracking_carrier_bottom .pp-link-underline');
        expectedDeliveryDate = result.querySelector('.PP-Section-ExpectedDeliveryDate .pp_tracking_info'); 
      }
    });
    if(document.querySelector('.tracking_numer_in_bar') && trackingNumber) document.querySelector('.tracking_numer_in_bar').innerHTML = trackingNumber ?  `<strong>Tracking No. </strong><br>  ${trackingNumber.textContent}` : "";
    if(document.querySelector('.tracking--date-pp') && expectedDeliveryDate) document.querySelector('.tracking--date-pp').textContent = expectedDeliveryDate ? expectedDeliveryDate.textContent : ""; 
 if (parcleTrackerTitle && !has_already_info) { 
      const htmlToAppend = `
        <div class="pp_tracking_result_info_wrapper">
          <div class="pp_tracking_result_info">
            <p class="pp_tracking_result_info_text">Thank you for your order!</p>
            <p class="pp_tracking_result_info_text">Expected Delivery Date <strong class="tracking--date-pp">${expectedDeliveryDate ? expectedDeliveryDate.textContent : "" }</strong></p>
            </div>
        
        <div class="pp_tracking_order-date-info-wrapper">
        ${trackingNumber ?  `<p class="pp_tracking_order_date_info_text tracking_numer_in_bar"> <strong>Tracking No. </strong><br>  ${trackingNumber.textContent}</p>` : ""} 

         <p class="pp_tracking_order_date_info_text"> <strong>Delivery Method </strong><br>Standard Shipping</p>
        </div>
        </div>
        </div>
      `;
      parcleTrackerTitle.insertAdjacentHTML('afterend', htmlToAppend);
    }
  }, 1000); 
}